import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  Mail, 
  Server, 
  Key, 
  TestTube, 
  Send, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Save,
  RefreshCw
} from 'lucide-react';
import emailService from '../services/emailService';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'react-hot-toast';

const EmailConfig = () => {
  const [formData, setFormData] = useState({
    host: '',
    port: 587,
    secure: false,
    user: '',
    password: '',
    from: '',
    loginUrl: 'https://farm.my-it.pl/login'
  });

  const [testEmail, setTestEmail] = useState('');
  const queryClient = useQueryClient();

  // Pobierz aktualną konfigurację
  const { data: configData, isLoading: configLoading, error: configError } = useQuery(
    'email-config',
    emailService.getConfig,
    {
      retry: 1,
      refetchOnWindowFocus: false
    }
  );

  // Aktualizuj formData gdy dane zostaną pobrane
  useEffect(() => {
    if (configData?.config) {
      console.log('📧 Pobrano konfigurację email:', configData.config);
      setFormData({
        host: configData.config.host || '',
        port: configData.config.port || 587,
        secure: configData.config.secure || false,
        user: configData.config.user || '',
        password: '', // Nie pokazuj hasła
        from: configData.config.from || '',
        loginUrl: configData.config.loginUrl || 'https://farm.my-it.pl/login'
      });
    } else if (configData && !configData.config) {
      console.log('⚠️ Brak konfiguracji email w bazie danych');
      // Resetuj formData do wartości domyślnych jeśli nie ma konfiguracji
      setFormData({
        host: '',
        port: 587,
        secure: false,
        user: '',
        password: '',
        from: '',
        loginUrl: 'https://farm.my-it.pl/login'
      });
    }
  }, [configData]);

  // Zapisz konfigurację
  const saveConfigMutation = useMutation(emailService.saveConfig, {
    onSuccess: () => {
      toast.success('Konfiguracja email zapisana pomyślnie');
      queryClient.invalidateQueries('email-config');
    },
    onError: (error) => {
      toast.error(`Błąd zapisywania konfiguracji: ${error.response?.data?.message || error.message}`);
    }
  });

  // Test połączenia
  const testConnectionMutation = useMutation(emailService.testConnection, {
    onSuccess: (data) => {
      if (data.success) {
        toast.success('Test połączenia SMTP pomyślny!');
      } else {
        toast.error(`Test połączenia nieudany: ${data.details}`);
      }
    },
    onError: (error) => {
      toast.error(`Błąd testowania połączenia: ${error.response?.data?.message || error.message}`);
    }
  });

  // Wyślij testowy email
  const sendTestMutation = useMutation(emailService.sendTestEmail, {
    onSuccess: () => {
      toast.success('Testowy email został wysłany!');
    },
    onError: (error) => {
      toast.error(`Błąd wysyłania testowego emaila: ${error.response?.data?.message || error.message}`);
    }
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) : value)
    }));
  };

  const handleSaveConfig = (e) => {
    e.preventDefault();
    saveConfigMutation.mutate(formData);
  };

  const handleTestConnection = () => {
    testConnectionMutation.mutate();
  };

  const handleSendTest = (e) => {
    e.preventDefault();
    if (!testEmail) {
      toast.error('Podaj adres email do testu');
      return;
    }
    sendTestMutation.mutate(testEmail);
  };

  const getTestResultIcon = () => {
    if (!configData?.config?.testResult) return null;
    
    switch (configData.config.testResult) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
  };

  if (configLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (configError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <XCircle className="h-5 w-5 text-red-500 mr-2" />
          <h3 className="text-lg font-semibold text-red-900">
            Błąd pobierania konfiguracji email
          </h3>
        </div>
        <p className="mt-2 text-sm text-red-700">
          {configError.response?.data?.message || configError.message || 'Nie udało się pobrać konfiguracji email'}
        </p>
        <button
          onClick={() => queryClient.invalidateQueries('email-config')}
          className="mt-4 btn-secondary flex items-center"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Spróbuj ponownie
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Mail className="h-6 w-6 mr-2 text-blue-600" />
            Konfiguracja serwera email
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Skonfiguruj serwer SMTP do wysyłania emaili z danymi logowania i uprawnieniami
          </p>
        </div>

        <form onSubmit={handleSaveConfig} className="p-6 space-y-6">
          {/* Komunikat gdy brak konfiguracji */}
          {configData && !configData.config && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold text-yellow-900">
                    Brak konfiguracji email w bazie danych
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    Wprowadź dane konfiguracji serwera SMTP poniżej i zapisz je.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Host SMTP */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Server className="h-4 w-4 inline mr-1" />
                Host SMTP
              </label>
              <input
                type="text"
                name="host"
                value={formData.host}
                onChange={handleInputChange}
                placeholder="smtp.gmail.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Port */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Port
              </label>
              <input
                type="number"
                name="port"
                value={formData.port}
                onChange={handleInputChange}
                placeholder="587"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Użytkownik */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Key className="h-4 w-4 inline mr-1" />
                Użytkownik
              </label>
              <input
                type="text"
                name="user"
                value={formData.user}
                onChange={handleInputChange}
                placeholder="twoj-email@gmail.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Hasło */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hasło
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Hasło aplikacji"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* Email nadawcy */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email nadawcy
              </label>
              <input
                type="email"
                name="from"
                value={formData.from}
                onChange={handleInputChange}
                placeholder="noreply@twoja-firma.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            {/* URL logowania */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL logowania
              </label>
              <input
                type="url"
                name="loginUrl"
                value={formData.loginUrl}
                onChange={handleInputChange}
                placeholder="https://farm.my-it.pl/login"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Secure checkbox */}
          <div className="flex items-center">
            <input
              type="checkbox"
              name="secure"
              checked={formData.secure}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              Użyj bezpiecznego połączenia (SSL/TLS)
            </label>
          </div>

          {/* Status testu */}
          {configData?.config?.lastTested && (
            <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-md">
              {getTestResultIcon()}
              <span className="text-sm text-gray-600">
                Ostatni test: {new Date(configData.config.lastTested).toLocaleString('pl-PL')}
              </span>
            </div>
          )}

          {/* Przyciski */}
          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={saveConfigMutation.isLoading}
              className="btn-primary flex items-center"
            >
              {saveConfigMutation.isLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Zapisz konfigurację
            </button>

            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testConnectionMutation.isLoading}
              className="btn-secondary flex items-center"
            >
              {testConnectionMutation.isLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <TestTube className="h-4 w-4 mr-2" />
              )}
              Testuj połączenie
            </button>
          </div>
        </form>
      </div>

      {/* Test wysyłania emaila */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Send className="h-5 w-5 mr-2 text-green-600" />
            Test wysyłania emaila
          </h3>
          <p className="mt-1 text-sm text-gray-600">
            Wyślij testowy email, aby sprawdzić czy konfiguracja działa poprawnie
          </p>
        </div>

        <form onSubmit={handleSendTest} className="p-6">
          <div className="flex space-x-4">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <button
              type="submit"
              disabled={sendTestMutation.isLoading}
              className="btn-primary flex items-center"
            >
              {sendTestMutation.isLoading ? (
                <LoadingSpinner size="sm" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Wyślij test
            </button>
          </div>
        </form>
      </div>

      {/* Przykłady konfiguracji */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-4">
          Przykłady konfiguracji popularnych dostawców:
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-4 rounded-md">
            <h4 className="font-semibold text-gray-900 mb-2">Zenbox.pl</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li><strong>Host:</strong> smtp.zenbox.pl</li>
              <li><strong>Port:</strong> 587</li>
              <li><strong>Secure:</strong> Nie (STARTTLS)</li>
              <li><strong>Uwaga:</strong> Port 587 z STARTTLS</li>
            </ul>
          </div>

          <div className="bg-white p-4 rounded-md">
            <h4 className="font-semibold text-gray-900 mb-2">Gmail</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li><strong>Host:</strong> smtp.gmail.com</li>
              <li><strong>Port:</strong> 587</li>
              <li><strong>Secure:</strong> Nie</li>
              <li><strong>Uwaga:</strong> Użyj hasła aplikacji</li>
            </ul>
          </div>

          <div className="bg-white p-4 rounded-md">
            <h4 className="font-semibold text-gray-900 mb-2">Outlook/Hotmail</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li><strong>Host:</strong> smtp-mail.outlook.com</li>
              <li><strong>Port:</strong> 587</li>
              <li><strong>Secure:</strong> Nie</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailConfig;
